// "My Day — automatic" — pure derivation of a subcontractor's personal times
// from the day's route start T (minutes since midnight, parsed from the depot's
// route file). No supabase imports: everything here is unit-testable maths.
//
// The step spec (keys, levels, offsets, guidance) is NO LONGER defined here —
// it lives once in src/lib/autoDaySpec.js and is mirrored for Deno in
// supabase/functions/_shared/auto-day-spec.ts. This module only turns the spec
// into a concrete, time-ordered plan for a given day.
//
// Offsets from route start T (see autoDaySpec.js for the coefficients):
//   auto:wake         T − 15 − travel − prep   (alarm)
//   auto:leave        T − 15 − travel          (push)
//   auto:arrive       T − 15                   (alarm)
//   auto:samsara      T − 10                   (push — reminder to use the
//                     contractor's Samsara app; never replicates the check)
//   auto:route_start  T                        (silent)
// Guidance-only, like protocols: nothing here is compliance tracking.

import { AUTO_DAY_SPEC, stepOffset, AUTO_DAY_FIRST_WEEK_GUIDANCE as FIRST_WEEK } from '@/lib/autoDaySpec';
import { trafficAdjustedTravel } from '@/lib/trafficModel';

// Back-compat re-export: existing callers/tests import AUTO_DAY_STEPS from here.
// It is exactly the canonical spec (same key order, levels, guidance).
export const AUTO_DAY_STEPS = AUTO_DAY_SPEC;

// settings ({auto_enabled, travel_minutes, travel_source, prep_minutes}) + route
// start (minutes since midnight) → [{key, title, level, minutes, guidance}]
// sorted by time. [] whenever the day is unresolvable: no route start, auto-day
// off, or travel still unknown — times are never guessed.
//
// options:
//   travelOverride  a per-day effective travel (minutes) that WINS over
//                   settings.travel_minutes — e.g. a persisted traffic-aware
//                   figure. Must be finite; otherwise ignored.
//   applyTraffic    when true (default) and travel_source is 'auto' and no
//                   explicit override is given, the free-flow travel is scaled
//                   by the time-of-day traffic model so wake/leave reflect the
//                   real morning. A 'manual' travel time is never scaled.
//   firstWeek       swap in the gentler first-week guidance strings.
export function resolveAutoDay(settings, routeStartMinutes, options = {}) {
  const start = Number(routeStartMinutes);
  if (routeStartMinutes == null || !Number.isFinite(start)) return [];
  if (!settings?.auto_enabled) return [];
  const baseTravel = Number(settings.travel_minutes);
  if (settings.travel_minutes == null || !Number.isFinite(baseTravel)) return [];
  const prep = settings.prep_minutes == null ? 45 : Number(settings.prep_minutes);
  const prepVal = Number.isFinite(prep) ? prep : 45;

  // Effective travel: explicit override → traffic-adjusted (auto only) → base.
  const { travelOverride, applyTraffic = true, firstWeek = false } = options;
  let travel = baseTravel;
  if (travelOverride != null && Number.isFinite(Number(travelOverride))) {
    travel = Number(travelOverride);
  } else if (applyTraffic) {
    travel = trafficAdjustedTravel(baseTravel, start, settings.travel_source || 'manual');
  }

  return AUTO_DAY_SPEC
    .map((s) => ({
      key: s.key,
      title: s.title,
      level: s.level,
      minutes: start + stepOffset(s, travel, prepVal),
      guidance: (firstWeek && FIRST_WEEK[s.key]) || s.guidance,
    }))
    .sort((a, b) => a.minutes - b.minutes);
}

// Minutes since midnight → 'HH:MM' (wrapped into 0–23:59), or null.
export function minutesToHHMM(minutes) {
  const n = Number(minutes);
  if (minutes == null || !Number.isFinite(n)) return null;
  const m = ((Math.round(n) % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}
